<template>
  <page-layout>
    <span slot="header-name">{{ $tf('events') }}</span>

    <span slot="header-detail">{{ $tf('match,,1') }}: {{ eventsParams.matched }}, {{ $tf('total') }}: {{ eventsParams.total }}</span>

    <common-tabs slot="header-actions" v-model="$route.params.objects" :items="eventsTabs" :converter="$tfo" @change="changeTab" />

    <div slot="header-content-actions">
      <i class="el-icon-loading" v-if="state.playing && $store.state.server.connected"></i>
      <el-button name="play-btn" size="small" type="primary" v-if="!state.playing" @click="togglePlaying" :disabled="!!state.filter.current.page"
        ><i class="fa fa-play"></i
      ></el-button>
      <el-button name="pause-btn" size="small" :plain="true" v-else @click="togglePlaying"><i class="fa fa-pause"></i></el-button>
      <el-button name="set-all-acknowledged-btn" class="mar-left--0-5" size="small" :plain="true" @click="setAllAcknowledged">
        <i class="fa fa-check"></i>
        <span class="mar-left--0-5">{{ $tfo('acknowledge_v | all') }}</span>
      </el-button>
      <el-select
        name="limit"
        class="filter--page-limit mar-left--1"
        v-model="state.filter.current.limit"
        :disabled="state.loading"
        v-if="state.limits"
        size="small"
      >
        <el-option :key="item" :value="item" :label="item" v-for="item in state.limits"></el-option>
      </el-select>
    </div>

    <table-navigation slot="header-content-navigation" :state="state"></table-navigation>

    <el-table
      slot="content"
      name="events-table"
      class="events-table"
      stripe
      :size="$vars.sizes.table || $vars.sizes.common"
      :data="state.items"
      row-key="id"
      @row-click="rowClick"
      v-loading="state.loading"
    >
      <el-table-column prop="id" :label="$tf('id')" width="80" class-name="events-table__id-cell">
        <template slot-scope="{ row }">
          <el-tooltip class="item" effect="dark" :content="row.id" placement="right-end">
            <span>{{ row.id }}</span>
          </el-tooltip>
          <copy-to-clipboard-button style="margin-top: 10px" :text="row.id" icon />
        </template>
      </el-table-column>

      <el-table-column prop="" :label="$tf('detected')" width="176">
        <template slot-scope="{ row }">
          <object-thumbnail :url="row.thumbnail" :frame="row.fullframe" @preview="showVideoPlayerOrFullFrame(row)"></object-thumbnail>
        </template>
      </el-table-column>

      <el-table-column prop="" :label="$tf('card')" min-width="376px">
        <div class="card" slot-scope="{ row }">
          <object-thumbnail
            style="width: 176px"
            :id="row.matched_object"
            :objects="objectsType"
            :requiredId="!!row.matched_card"
            @preview="(v) => showSourceImage(v && v.src)"
          ></object-thumbnail>
          <div>
            <card-item v-if="row.matched_card" :id="row.matched_card" :eventId="row.id" :confidence="row.confidence" :objects="objectsType"></card-item>
            <div v-else>
              <router-link name="item-btn" :to="{ path: getItemLink(row) }">{{ $tf(['no', 'matches,, 0']) }}</router-link>
            </div>
            <watch-lists-inline links :objects-type="objectsType" :ids="row.matched_lists" :filter="state.filter.current" />
            <div class="event-reaction-content">
              <event-reaction :item="row" :objects="objectsType" :always-display-cancel="false" />
            </div>
            <span v-if="$store.getters.hasMatchClusters && row.matched_cluster" @click.stop
              ><router-link :to="getClusterEventsUrl(row.matched_cluster)">{{ $tf('similar_cluster') }}: {{ row.matched_cluster }}</router-link></span
            >
          </div>
        </div>
      </el-table-column>

      <el-table-column width="192px" :label="$tf('attributes')">
        <template slot-scope="{ row }">
          <features :features="row.features" :objects-type="objectsType" />
          <div v-if="$store.getters.features.temperature">{{ $tf('temperature') }}: {{ row.temperature ? row.temperature : '-' }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="role" width="160px" :label="`${$tf('exact_time')}, ${$tf('camera')}`">
        <template slot-scope="{ row }">
          {{ row.created_date | isoStringToDate | formatDate }}
          <br />
          {{ row.created_date | isoStringToDate | formatTime }}
          <br /><br />

          <el-tooltip
            class="tooltip-item"
            effect="dark"
            :content="getCameraGroup(row).name + ' (' + getCamera(row).name + ')'"
            placement="top"
            :open-delay="800"
          >
            <div>
              <span class="link" @click.stop="addArrayFilter('camera_groups', getCamera(row).group)">{{ getCameraGroup(row).name | shortString }}</span>
              (<span class="link" @click.stop="addArrayFilter('cameras', getCamera(row).id)">{{ $filters.shortString(getCamera(row).name, 10) }}</span
              >)
            </div>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!--
      demo generator
      <table-generator slot="table" :state="state" :objects-type="objectsType"></table-generator>
    !-->

    <table-navigation slot="footer-content-navigation" :state="state"></table-navigation>
  </page-layout>
</template>

<script>
import _ from '@/apps/common/lodash';
import qs from 'qs';
import Vue from 'vue';
import { ObjectsTypeSingleForm } from '@/store/objects/get.module';
import TableNavigation from '@/components/tables/navigation.vue';
import WatchListsInline from '../watch-lists/inline.vue';
import ObjectsInline from '../objects/inline.vue';
import ObjectThumbnail from '../objects/thumbnail.vue';
import CardItem from './card-item.vue';
import Features from '../common/features.vue';
import EventReaction from './reaction.vue';
import PageLayout from '@/components/page/layout';
import TableGenerator from '@/components/tables/generator';
import CopyToClipboardButton from '@/components/common/copyToClipboardButton';
import { getEventBBox, ImageType } from '@/store/actions/showImageByType';

function alphabeticalSort(a, b) {
  return a.localeCompare(b);
}
export default {
  components: {
    CopyToClipboardButton,
    TableGenerator,
    PageLayout,
    Features,
    EventReaction,
    CardItem,
    ObjectThumbnail,
    WatchListsInline,
    ObjectsInline,
    TableNavigation
  },
  name: 'page-events',
  computed: {
    state() {
      return this.$store.state[this.stateName];
    },
    objectsType() {
      const { objects } = this.$route.params;
      const objectsType = objects ?? 'faces';
      return objectsType;
    },
    stateName() {
      return `${this.objectsType}_events`;
    },
    statisticName() {
      return ObjectsTypeSingleForm[this.objectsType] + '_events';
    },
    eventsParams() {
      return this.$store.state.stats[this.statisticName];
    },
    eventsTabs() {
      return this.$store.getters.enabledObjects
        .filter((objectsType) => !this.$hasNoPermission(computeObjectEventsViewPermissionStringByObjectsType(objectsType)))
        .map((item) => ({ i18n: item, name: item }));
    }
  },
  mounted() {
    this.$cache.enable(true);
    this.$store.dispatch(this.$store.state.camera_groups.Action.Get);
    this.$store.dispatch(this.$store.state.cameras.Action.GetAll);
    this.$store.dispatch(this.$store.state.watch_lists.Action.Get);
    this.loadStats();
  },
  beforeDestroy() {
    clearInterval(this.statsInterval);
    this.$cache.enable(false);
  },
  watch: {
    stateName() {
      this.loadStats();
    }
  },
  methods: {
    loadStats() {
      clearInterval(this.statsInterval);
      this.$store.dispatch('getStats', this.statisticName);
      this.statsInterval = setInterval(() => this.$store.dispatch('getStats', this.statisticName), 10000);
    },
    rowClick(item) {
      if (this.$store.state.app.key.ctrl) return;
      if (this.$store.state.app.key.alt) {
        this.$store.dispatch('showInfo', { item, formattedData: this.getFormattedData(item) });
        return;
      }

      this.$router.push({ path: this.getItemLink(item) });
    },
    showEventImage(item) {
      this.$store.dispatch('showImageByType', { type: ImageType.Event, objectsType: this.objectsType, data: item });
    },
    showSourceImage(source) {
      this.$store.dispatch('showImageByType', { type: ImageType.Source, objectsType: this.objectsType, data: source });
    },
    showVideoPlayerOrFullFrame(row) {
      this.$store.dispatch('showVideoPlayerOrFullFrame', { item: row, objectsType: this.objectsType, orFullFrame:false });
    },
    getCamera(item) {
      return this.$store.state.cameras.items.find((v) => v.id === item.camera) || { name: this.$tf('camera | not_found,,1') };
    },
    getCameraGroup(item) {
      return this.$store.state.camera_groups.items.find((v) => v.id === item.camera_group) || { name: '' };
    },
    togglePlaying() {
      this.state.playing = !this.state.playing;

      if (this.state.playing) {
        this.state.filter.current.created_date_lte = null;
      } else {
        this.state.filter.current.created_date_lte = new Date().toISOString();
      }
    },
    getItemLink(item) {
      return `/events/${this.objectsType}/${encodeURIComponent(item.id)}/`;
    },
    getClusterEventsUrl(id) {
      return `/cluster-events/${this.objectsType}/filter/cluster_in=${id}&limit=10`;
    },
    getFilterPath(filter) {
      return `#/events/${this.objectsType}/filter/` + qs.stringify(Object.assign(filter, { limit: 20 }), { sort: alphabeticalSort });
    },
    addArrayFilter(name, id) {
      if (this.state.filter.current[name].indexOf(id) === -1) this.state.filter.current[name].push(id);
    },
    setAllAcknowledged() {
      this.$confirm(this.$tf(['set_all_acknowledged_confirm']), this.$tf(['warning']), {
        confirmButtonText: this.$tf(['ok']),
        cancelButtonText: this.$tf(['cancel']),
        type: 'warning'
      })
        .then(() => {
          this.doMakeEventsAcknowledgedRequest()
            .then(() => this.$store.dispatch(this.state.Action.Get))
            .then(() => {
              this.$notify({ type: 'success', message: this.$tf(['action', 'success']) });
            })
            .catch((e) => {
              this.$notify({ duration: 0, message: this.$createElement('message-box', { props: { e: e } }) });
            });
        })
        .catch(() => {
          console.warn('[acknowledge] was cancelled');
        });
    },
    toggleAcknowledged(item) {
      let instance = _.pick(item, ['acknowledged', 'id']);
      instance.acknowledged = !item.acknowledged;
      Vue.set(item, 'loading', true);
      this.$store
        .dispatch(this.state.Action.Update, instance)
        .then(() => {
          item.acknowledged = instance.acknowledged;
          Vue.set(item, 'cancelAcknowledged', true);
          item.loading = false;
          this.$notify({ type: 'success', message: this.$tf(['action', 'success']) });
        })
        .catch((e) => {
          item.loading = false;
          this.$notify({ duration: 0, message: this.$createElement('message-box', { props: { e: e } }) });
        });
    },
    changeTab(name) {
      this.$router.push({ name: 'events', params: { objects: name, filter: 'limit=20' } });
    },
    doMakeEventsAcknowledgedRequest() {
      return this.$store.dispatch('requestApi', {
        model: `events/${this.objectsType}/acknowledge`,
        method: 'POST'
      });
    },
    getFormattedData(event) {
      const bbox = getEventBBox(event);
      return {
        width: bbox.w,
        height: bbox.h,
        size: Math.round(Math.sqrt(bbox.w * bbox.h)),
        quality: event.quality
      };
    }
  }
};

function computeObjectEventsViewPermissionStringByObjectsType(objectsType) {
  return `ffsecurity.view_${ObjectsTypeSingleForm[objectsType]}event`;
}
</script>

<style lang="stylus">
.event-reaction-content {
  margin-top: 1rem;
}

.filter--page-limit {
  display: inline-block;
  width: 5rem;
}

.tag-loading {
  cursor: none !important;
  opacity: 0.2;
}

.card {
  display: flex;
}

.events-table {
  &__id-cell > .cell {
    width: 5rem;
    direction: rtl;
  }
}
</style>
